import { useState } from 'react';
import { galleryImages } from '../../mocks/books';
import { partnerFaq } from '../../mocks/homeSections';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import HonestyStatement from './components/HonestyStatement';
import Seo from '@/components/feature/Seo';
import { pageSeo } from '@/mocks/seo';

const aboutPageSchema = {
  '@type': 'AboutPage',
  name: 'About Leavitt Peak Press',
  url: 'https://leavittpeakpress.com/about-us',
  description:
    'Who Leavitt Peak Press is, what we control, what we never claim, and the commitments authors can hold us to.',
  mainEntity: {
    '@type': 'Organization',
    name: 'Leavitt Peak Press',
    url: 'https://leavittpeakpress.com/',
  },
};

const faqStructuredData = {
  '@type': 'FAQPage',
  mainEntity: partnerFaq.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

const aboutSchema = [aboutPageSchema, faqStructuredData];

export default function AboutUs() {
  const [currentGallery, setCurrentGallery] = useState(0);
  const galleryPerPage = 3;
  const gallerySlides = Math.ceil(galleryImages.length / galleryPerPage);

  const nextGallery = () => setCurrentGallery((prev) => (prev + 1) % gallerySlides);
  const prevGallery = () => setCurrentGallery((prev) => (prev - 1 + gallerySlides) % gallerySlides);

  const heroAnim = useScrollAnimation({ threshold: 0.15 });
  const aboutAnim = useScrollAnimation();
  const expertAnim = useScrollAnimation();
  const galleryAnim = useScrollAnimation();
  const dealsAnim = useScrollAnimation({ threshold: 0.2 });

  return (
    <main>
      <Seo {...pageSeo.aboutUs} schema={aboutSchema} />

      {/* Hero Section */}
      <section className="w-full py-12 md:py-16 bg-cream-100 overflow-hidden">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={heroAnim.ref} className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-12 animate-on-scroll ${heroAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="flex-1">
              <p className="text-xs text-navy-500 uppercase tracking-wider mb-2">Who we are?</p>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900 mb-4">
                We turn your vision into reality—<span className="text-navy-500">one expert step at a time</span>
              </h1>
              <div className="flex flex-wrap gap-3 mt-6">
                <a
                  href="/contact-us"
                  className="px-6 py-3 text-sm font-medium text-navy-500 border border-navy-500 rounded-full hover:bg-navy-50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  Contact Us
                </a>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="w-full max-w-[450px] animate-float">
                <img
                  src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/Banner-Image-2.png"
                  alt="About Leavitt Peak Press"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Leavitt Peak Press */}
      <section className="w-full py-12 md:py-16 bg-cream-200">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={aboutAnim.ref} className={`animate-on-scroll ${aboutAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="text-center mb-8">
              <p className="text-xs text-navy-500 uppercase tracking-wider mb-2">About Us</p>
              <h2 className="text-2xl md:text-3xl font-bold text-navy-900">Leavitt Peak Press</h2>
            </div>
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
              <div className="flex-1 flex items-center justify-center">
                <div className="w-full max-w-[300px] animate-float">
                  <img
                    src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/Leavitt-Peak-Press-Logo.png"
                    alt="Leavitt Peak Press Logo"
                    className="w-full h-auto"
                  />
                </div>
              </div>
              <div className="flex-1">
                <div className="text-sm text-navy-700 leading-relaxed space-y-4">
                  <p>
                    At Leavitt Peak Press, we believe every author deserves more than just a published book—they deserve a long-term publishing partner. Our goal is to guide you through every stage of your self-publishing journey, from manuscript preparation and professional production to branding, distribution, marketing, and long-term audience growth.
                  </p>
                  <p>
                    Your journey begins with one of our Publishing and Marketing Consultants, who will take the time to understand your goals, your book, and your vision as an author. They will work with you to develop a customized publishing and marketing strategy designed to position your book for today's highly competitive marketplace.
                  </p>
                  <p>
                    Once your publishing plan is in place, you will be assigned a dedicated Portfolio Manager who will serve as your primary point of contact throughout the process. Your Portfolio Manager coordinates every aspect of your project, ensuring clear communication, timely updates, and seamless collaboration across our production and marketing teams.
                  </p>
                  <p>
                    Behind the scenes, a team of experienced editors, designers, formatters, branding specialists, digital marketers, advertising professionals, distribution coordinators, and publishing specialists work together to bring your vision to life. Every stage is carefully managed to ensure your book is professionally produced, widely available through major retail and distribution channels, and supported by marketing initiatives that help increase its visibility and connect it with the right audience.
                  </p>
                  <p>
                    At Leavitt Peak Press, our commitment extends beyond publishing a book. We are dedicated to helping authors build a professional author brand, expand their reach, and create meaningful opportunities for long-term success in today's evolving self-publishing industry.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Honesty Statement */}
      <HonestyStatement />

      {/* Our Publishing Experts */}
      <section className="w-full py-12 md:py-16 bg-navy-500">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={expertAnim.ref} className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-12 animate-on-scroll ${expertAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Our Publishing Experts</h2>
              <div className="text-sm text-navy-100 leading-relaxed space-y-4">
                <p>
                  At Leavitt Peak Press, we believe every book has the potential to make an impact when it is supported by the right publishing strategy. As an independent self-publishing company, we help authors transform their manuscripts into professionally published books while providing the guidance and resources needed to navigate today's publishing landscape.
                </p>
                <p>
                  Our team brings together professionals in editorial, design, production, distribution, and book marketing, working collaboratively to deliver high-quality publishing services tailored to each author's goals. Whether an author is publishing their first book or expanding an established catalog, we focus on providing practical solutions that help their work reach readers worldwide.
                </p>
                <p>
                  We understand that every publishing journey is unique. That's why we emphasize clear communication, personalized support, and a collaborative approach throughout every stage of the process. From manuscript preparation and book production to global distribution and promotional opportunities, our mission is to help authors publish with confidence and present their work professionally.
                </p>
                <p>
                  At Leavitt Peak Press, we are committed to building lasting relationships with our authors by providing reliable publishing services, professional guidance, and opportunities that support their long-term growth in the ever-evolving self-publishing industry.
                </p>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="w-full max-w-[450px] animate-float-delayed">
                <img
                  src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/Banner-4.png"
                  alt="Publishing Experts"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery - To The World */}
      <section className="w-full py-12 md:py-16 bg-navy-500">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={galleryAnim.ref} className={`animate-on-scroll ${galleryAnim.isVisible ? 'animate-visible' : ''}`}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Leavitt Peak Press <span className="text-navy-200">To The World</span>
            </h2>
            <p className="text-sm text-navy-100 leading-relaxed mb-8 max-w-4xl">
              At Leavitt Peak Press, we're not just a publishing house; we're your dedicated partner in literary success. We go above and beyond to ensure our authors' works receive the recognition they deserve. From participating in prestigious international book fairs to orchestrating captivating book signings and engaging directly with businesses and clients, we leave no stone unturned in our mission to help our authors achieve their publishing and marketing goals.
            </p>

            <div className="relative">
              <div className="flex items-center gap-4">
                <button
                  onClick={prevGallery}
                  className="hidden md:flex w-10 h-10 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-all duration-300 hover:scale-110 shrink-0"
                  aria-label="Previous"
                >
                  <i className="ri-arrow-left-s-line text-white"></i>
                </button>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {galleryImages
                    .slice(currentGallery * galleryPerPage, currentGallery * galleryPerPage + galleryPerPage)
                    .map((img, idx) => (
                      <div key={idx} className="overflow-hidden rounded-lg relative group">
                        <img
                          src={img}
                          alt={`Gallery ${idx + 1}`}
                          className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-navy-900/0 group-hover:bg-navy-900/20 transition-colors duration-500 pointer-events-none"></div>
                      </div>
                    ))}
                </div>
                <button
                  onClick={nextGallery}
                  className="hidden md:flex w-10 h-10 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-all duration-300 hover:scale-110 shrink-0"
                  aria-label="Next"
                >
                  <i className="ri-arrow-right-s-line text-white"></i>
                </button>
              </div>
              <div className="flex md:hidden items-center justify-center gap-4 mt-4">
                <button onClick={prevGallery} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:scale-110 transition-transform">
                  <i className="ri-arrow-left-s-line text-white"></i>
                </button>
                <button onClick={nextGallery} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:scale-110 transition-transform">
                  <i className="ri-arrow-right-s-line text-white"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Special Deals Section */}
      <section className="w-full py-12 md:py-16 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 transition-transform duration-[20s] hover:scale-110">
          <img
            src="https://mlhdhysslvu0.i.optimole.com/w:auto/h:auto/q:mauto/ig:avif/https://leavittpeakpress.com/wp-content/uploads/2025/02/Books.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div ref={dealsAnim.ref} className={`w-full px-4 md:px-6 lg:px-8 relative z-10 animate-on-scroll ${dealsAnim.isVisible ? 'animate-visible' : ''}`}>
          <div className="max-w-2xl">
            <p className="text-xs text-navy-300 uppercase tracking-wider mb-2">Looking for deals?</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">Check out our special deals on books!</h2>
            <a
              href="https://www.amazon.com/s?k=leavitt+peak+press&i=stripbooks&rh=n%3A283155%2Cp_72%3A1250221011"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex px-6 py-3 text-sm font-medium text-navy-900 bg-white rounded-full hover:bg-cream-100 transition-all duration-300 hover:scale-105"
            >
              Check what's on sale
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}